import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import { useState } from 'react';
import { Button, Dialog, DialogActions, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import SpinnerComponent from 'modules/common/spinner.component';
import { getClientBillingPoliciesByClientId, postClientBillingPolicy } from 'modules/clients/clients.actions';
import { showToast } from 'modules/layout/layout.actions';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { GET_CLIENT_BILLING_POLICIES_BY_CLIENT_ID_FAILURE, POST_CLIENT_BILLING_POLICY_FAILURE } from 'modules/clients/types';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';

const headCells = [
  {
    id: 'billingPolicyDescription',
    numeric: false,
    disablePadding: true,
    label: 'Billing Policy Description',
  },
  {
    id: 'clientPolicyStatusId',
    numeric: true,
    disablePadding: false,
    label: 'Status',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        }
      ]}
    >

        <Typography
          sx={{ 
            flex: '1 1 100%',
            fontSize: '1.3125rem',
            fontWeight: 'bold'
          }}
          variant="h5"
          id="tableTitle"
          component="div"
        >
          Billing Policies
        </Typography>

        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
    </Toolbar>
  );
}

const BillingPolicyTable = (props) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showInactivePolicies, setShowInactivePolicies] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [billingPolicyDescription, setBillingPolicyDescription] = useState('');
  const [clientPolicyStatusId, setClientPolicyStatusId] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected.push(id);
      props.newBillingPolicySelected(id); // billingPolicy.billingPolicyId
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      //newSelected = newSelected.concat(
       // selected.slice(0, selectedIndex),
       // selected.slice(selectedIndex + 1),
     // );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredPolicies = React.useMemo(
    () => (props.billingPolicies || []).filter(({ billingPolicy }) => {
      const statusId = billingPolicy.clientPolicyStatusId;
      const statusTitle = props.enumerations.clientPolicyStatuses?.find(status => status.id === statusId)?.title;
      const status = String(statusTitle || statusId).toLowerCase();
      return status === 'active' || (showInactivePolicies && status === 'inactive');
    }),
    [props.billingPolicies, props.enumerations.clientPolicyStatuses, showInactivePolicies],
  );
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filteredPolicies.length / rowsPerPage) - 1));
  const visibleRows = filteredPolicies.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    currentPage > 0 ? Math.max(0, (1 + currentPage) * rowsPerPage - filteredPolicies.length) : 0;

  const createBillingPolicy = async () => {
    setIsLoading(true);

    let newValues = {
        clientRecordId: props.clientRecordId,
        billingPolicyDescription: billingPolicyDescription,
        clientPolicyStatusId: clientPolicyStatusId,
        addedBy: 1,
        addedOn: new Date(),
        modifiedBy: 1,
        modifiedOn: new Date()
    };

    const resp = await props.postClientBillingPolicy(newValues);
    if (resp.type === POST_CLIENT_BILLING_POLICY_FAILURE) {
        showToast('Failed to save policy, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
    } else {
        const getResp = await props.getClientBillingPoliciesByClientId(props.clientRecordId);
        if (getResp.type === GET_CLIENT_BILLING_POLICIES_BY_CLIENT_ID_FAILURE) {
            setIsLoading(false);
            return false;
        } else {
            setIsLoading(false);
            setBillingPolicyDescription('');
            setClientPolicyStatusId(null);
            setCreateModalOpen(false);
            showToast('Successfully saved policy changes.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
            return true;
        }
    }
  }

  React.useEffect(() => {
    if (props.reloPolicySelected) setSelected([]);
  }, [props.reloPolicySelected]);

  return (
    <Box sx={{ width: '100%', padding: '10px' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} />

        <Box sx={{ paddingLeft: '16px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button color="primary" onClick={() => setCreateModalOpen(true)} variant="contained">
            Add Policy
          </Button>
          <FormControlLabel
            label="Show inactive policies"
            control={
              <Checkbox
                checked={showInactivePolicies}
                onChange={event => {
                  setShowInactivePolicies(event.target.checked);
                  setPage(0);
                }}
              />
            }
          />
        </Box>

        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={filteredPolicies.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected.includes(row.billingPolicy.billingPolicyId);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.billingPolicy.billingPolicyId)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    {/* <TableCell component="th" id={labelId} scope="row" padding="none">{row.billingPolicy.billingPolicyId}</TableCell> */}
                    <TableCell component="th" id={labelId} scope="row" padding="none">{row.billingPolicy.billingPolicyDescription}</TableCell>
                    <TableCell align="right">{props.enumerations.clientPolicyStatuses?.find((cps) => cps.id === row.billingPolicy.clientPolicyStatusId)?.title || row.billingPolicy.clientPolicyStatusId}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPolicies.length}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={createModalOpen} fullWidth={false} maxWidth={'lg'}>
        <Box sx={{ padding: '16px' }}>
            <Box sx={{ maxWidth: '100% !important', display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <div className="row justify-space-between align-center">
                    <Box sx={{display: 'flex', flexDirection: 'row'}}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px'
                            }}
                        >
                        <Typography
                            variant="h5"
                            sx={{
                                fontSize: '1.3125rem',
                                fontWeight: 'bold'
                            }}
                        >
                            New Billing Policy 
                        </Typography>
                        </Box>
                    </Box>
                </div>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', padding: '20px' }}>
                <Box sx={{ paddingRight: '16px' }}>
                    <TextField
                        label="Billing Policy Description"
                        name="billingPolicyDescription"
                        value={billingPolicyDescription}
                        margin="dense"
                        sx={{ maxWidth: '360px' }}
                        variant="standard"
                        type="text"
                        onChange={(e) => setBillingPolicyDescription(e.target.value)}
                    />
                </Box>
                <Box>
                    <FormControl sx={{ marginTop: '6px' }}>
                        <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }}>Billing Policy Status</InputLabel>
                        <Select
                            label="Currency"
                            name="clientPolicyStatusId"
                            value={clientPolicyStatusId}
                            variant="standard"
                            sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                            onChange={(e) => setClientPolicyStatusId(e.target.value)}
                        >
                            {props.enumerations.clientPolicyStatuses.map(opt => (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </Box>
        <DialogActions sx={{ p: '1.25rem' }}>
          {isLoading && <SpinnerComponent />}
          {!isLoading && (
            <> 
              <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button
                onClick={createBillingPolicy}
                color="primary"
                type="submit"
                variant="contained"
                disabled={billingPolicyDescription.length === 0 || clientPolicyStatusId === null}
              >
                Create Policy
              </Button>
            </>
          )} 
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default compose(
  connect(null, {
    getClientBillingPoliciesByClientId,
    postClientBillingPolicy,
    showToast
  }),
)(BillingPolicyTable);